// =============================================================================
// Widget bodies for the planning categories: calendar (day / week / month /
// agenda), birthdays, to-dos and shopping lists.
//
// Every calendar widget resolves its own scope (`personal` or a shared
// calendar id), so two calendar widgets can show two different calendars side
// by side. Derived entries (birthdays, linked to-dos) follow the same rules as
// Calendar.js — they are computed, never stored, so ticking a task off
// crosses it out here immediately.
// =============================================================================

import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  format,
  parseISO,
  isToday,
  isPast,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  differenceInCalendarDays,
  getWeek
} from 'date-fns';
import { formatDate as fmt } from '../../../lib/dates';
import { Gift, CheckSquare, Square, Check } from 'lucide-react';
import { expandEvents } from '../../../utils/recurrence';
import { defaultData } from '../../../utils/storage';
import { playBlub } from '../../../lib/sounds';
import { haptics } from '../../../lib/haptics';
import { priorityEmoji } from '../../../lib/priorities';
import { StatTile, WidgetEmpty, WidgetList, WidgetRow, isCompact } from '../shared';

const dateStr = (d) => format(d, 'yyyy-MM-dd');

// How many chip rows fit into the element `ref` points at. The week widget
// fills whatever height the dashboard gives it, so the count changes when
// the widget is resized; the last slot becomes "+N" when not everything
// fits. jsdom has no ResizeObserver — there the old four rows stay.
const useFitRows = (ref, pitch, fallback = 4) => {
  const [rows, setRows] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setRows(Math.max(0, Math.floor((el.clientHeight + 2) / pitch)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, pitch]);
  return rows;
};

// ---------------------------------------------------------------------------
// Scope-aware calendar data (events + birthdays + linked to-dos).
// ---------------------------------------------------------------------------
const useCalendarScope = (data, scope, today) => {
  const isShared = !!scope && scope !== 'personal';
  const sharedId = isShared ? scope : null;

  const categories = useMemo(() => {
    const all = data?.calendar?.categories || [];
    const scoped = all.filter((c) => (c.sharedCalendarId || null) === sharedId);
    if (!isShared) return scoped.length ? scoped : defaultData.calendar.categories;
    // Shared calendars only persist customised rows — overlay them on the
    // defaults so every colour resolves.
    const byId = new Map(scoped.map((c) => [c.id, c]));
    return defaultData.calendar.categories.map((d) => byId.get(d.id) || d);
  }, [data?.calendar?.categories, isShared, sharedId]);

  const getCategory = useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    return (id) => byId.get(id) || categories[0];
  }, [categories]);

  const events = useMemo(() => {
    const all = data?.calendar?.events || [];
    const scoped = all.filter((ev) => (ev.sharedCalendarId || null) === sharedId);
    const from = addDays(startOfWeek(startOfMonth(today), { weekStartsOn: 1 }), -7);
    const to = addDays(endOfWeek(endOfMonth(today), { weekStartsOn: 1 }), 14);
    const expanded = expandEvents(scoped, from, to);

    // --- birthdays -------------------------------------------------------
    let bdaySettings;
    if (isShared) {
      const cal = (data?.calendar?.sharedCalendars || []).find((c) => c.id === sharedId);
      bdaySettings = {
        linked: !!cal?.hasBirthdays && cal?.birthdayLinked !== false,
        categoryId: cal?.birthdayCategoryId || 'purple'
      };
    } else {
      const bs = data?.calendar?.birthdaySettings || {};
      bdaySettings = { linked: !!bs.linkedToCalendar, categoryId: bs.categoryId || 'purple' };
    }
    const birthdayEvents = [];
    if (bdaySettings.linked) {
      const people = (data?.calendar?.birthdays || []).filter(
        (b) => (b.sharedCalendarId || null) === sharedId
      );
      const years = [today.getFullYear(), today.getFullYear() + 1];
      for (const p of people) {
        let day = p.day, month = p.month, year = p.year;
        if (!day && p.birthDate) {
          const parts = String(p.birthDate).split('-').map(Number);
          year = parts[0]; month = parts[1]; day = parts[2];
        }
        if (!day || !month) continue;
        for (const yr of years) {
          // Via Date so Feb 29 rolls to Mar 1 in non-leap years.
          const d = new Date(yr, month - 1, day);
          const age = year ? yr - year : null;
          birthdayEvents.push({
            id: `birthday-${p.id}-${yr}`,
            title: `${p.name}${age && age > 0 ? ` (${age})` : ''}`,
            date: dateStr(d),
            endDate: dateStr(d),
            allDay: false,
            startTime: '',
            categoryId: bdaySettings.categoryId,
            isBirthday: true
          });
        }
      }
    }

    // --- linked to-dos ---------------------------------------------------
    const todoSettings = data?.todo?.calendarSettings || {};
    const own = data?.todo?.tasks || [];
    const foreign = data?.todo?.sharedTasks || [];
    let todoSource = [];
    if (isShared) {
      todoSource = [...own, ...foreign].filter((tk) => tk.sharedCalendarId === sharedId);
    } else if (todoSettings.linkedToCalendar && !todoSettings.sharedCalendarId) {
      todoSource = own.filter((tk) => !tk.sharedCalendarId);
    }
    const todoEvents = todoSource
      .filter((tk) => tk.date)
      .map((tk) => ({
        id: `todo-${tk.id}`,
        title: tk.title,
        date: tk.date,
        endDate: tk.date,
        allDay: false,
        startTime: tk.time || '',
        categoryId: tk.calendarCategoryId || todoSettings.categoryId || 'blue',
        isTodo: true,
        done: !!tk.done,
        priority: tk.priority || null
      }));

    return [...expanded, ...birthdayEvents, ...todoEvents];
  }, [data, isShared, sharedId, today]);

  const eventsForDay = useMemo(() => {
    const byDay = new Map();
    for (const ev of events) {
      if (ev.endDate && ev.endDate !== ev.date) {
        // Multi-day: index every day it covers (bounded by the window).
        let cursor = ev.date;
        let guard = 0;
        while (cursor <= ev.endDate && guard < 400) {
          if (!byDay.has(cursor)) byDay.set(cursor, []);
          byDay.get(cursor).push(ev);
          cursor = dateStr(addDays(parseISO(cursor), 1));
          guard++;
        }
      } else {
        if (!byDay.has(ev.date)) byDay.set(ev.date, []);
        byDay.get(ev.date).push(ev);
      }
    }
    for (const list of byDay.values()) {
      list.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    }
    return (d) => byDay.get(dateStr(d)) || [];
  }, [events]);

  return { events, getCategory, eventsForDay };
};

// Shared row rendering for a single event in a list-style widget.
const EventRow = ({ ev, cat, t }) => (
  <div className="flex items-stretch gap-2.5 px-4 py-2 min-w-0">
    <div className="flex flex-col items-end justify-center w-11 flex-shrink-0 text-xs font-medium leading-tight">
      {ev.isBirthday ? (
        <Gift size={16} style={{ color: cat?.color }} />
      ) : ev.isTodo ? (
        <>
          <CheckSquare size={16} style={{ color: cat?.color }} />
          {ev.startTime && <span className="text-gray-900 dark:text-gray-100 mt-0.5">{ev.startTime}</span>}
        </>
      ) : ev.allDay !== false ? (
        <span className="text-gray-400 text-[10px]">{t('dashboard.allDay', 'ganztägig')}</span>
      ) : (
        <>
          <span className="text-gray-900 dark:text-gray-100">{ev.startTime || '—'}</span>
          {ev.endTime && <span className="text-gray-400 mt-0.5">{ev.endTime}</span>}
        </>
      )}
    </div>
    <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: cat?.color }} />
    <div className="flex-1 min-w-0 flex items-center gap-1.5">
      <span
        className={`text-sm font-medium truncate flex-1 min-w-0 ${
          ev.isTodo && ev.done
            ? 'line-through text-gray-400'
            : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {ev.title}
      </span>
      {priorityEmoji(ev.priority) && (
        <span className="flex-shrink-0 leading-none pr-0.5">{priorityEmoji(ev.priority)}</span>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// calendar.day
// ---------------------------------------------------------------------------
const CalendarDay = ({ data, scope, today, t }) => {
  const { getCategory, eventsForDay } = useCalendarScope(data, scope, today);
  const items = eventsForDay(today);
  if (items.length === 0) {
    return <WidgetEmpty>{t('dashboard.emptyDay', 'Heute nichts geplant.')}</WidgetEmpty>;
  }
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {items.map((ev) => (
        <EventRow key={ev.id} ev={ev} cat={getCategory(ev.categoryId)} t={t} />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// calendar.agenda — the next N upcoming entries, grouped by day.
// ---------------------------------------------------------------------------
const CalendarAgenda = ({ data, scope, size, today, t }) => {
  const { events, getCategory } = useCalendarScope(data, scope, today);
  const todayStr = dateStr(today);
  const upcoming = useMemo(() => {
    return events
      .filter((ev) => (ev.endDate || ev.date) >= todayStr)
      .sort((a, b) => {
        const byDate = a.date.localeCompare(b.date);
        if (byDate) return byDate;
        return (a.startTime || '').localeCompare(b.startTime || '');
      })
      .slice(0, isCompact(size) ? 5 : 12);
  }, [events, todayStr, size]);

  if (upcoming.length === 0) {
    return <WidgetEmpty>{t('dashw.noUpcoming', 'Keine kommenden Termine.')}</WidgetEmpty>;
  }

  let lastDay = null;
  return (
    <div>
      {upcoming.map((ev) => {
        const showHeader = ev.date !== lastDay;
        lastDay = ev.date;
        const d = parseISO(ev.date);
        const diff = differenceInCalendarDays(d, today);
        return (
          <div key={ev.id}>
            {showHeader && (
              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {diff === 0
                  ? t('common.today', 'Heute')
                  : diff === 1
                  ? t('dashw.tomorrow', 'Morgen')
                  : fmt(d, 'weekdayLong')}
              </div>
            )}
            <EventRow ev={ev} cat={getCategory(ev.categoryId)} t={t} />
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// calendar.week — the week grid (Mon–Sun) with multi-day bars.
// ---------------------------------------------------------------------------
const CalendarWeek = ({ data, scope, today, t }) => {
  const { events, getCategory, eventsForDay } = useCalendarScope(data, scope, today);
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const week = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const multiDayBars = useMemo(() => {
    const weekStartStr = dateStr(week[0]);
    const weekEndStr = dateStr(week[6]);
    const candidates = events
      .filter(
        (ev) =>
          ev.allDay !== false &&
          ev.endDate &&
          ev.endDate !== ev.date &&
          ev.date <= weekEndStr &&
          ev.endDate >= weekStartStr
      )
      .map((ev) => {
        const startIdx = week.findIndex((d) => dateStr(d) >= ev.date);
        const endIdxRev = [...week].reverse().findIndex((d) => dateStr(d) <= ev.endDate);
        return {
          event: ev,
          startCol: Math.max(0, startIdx),
          endCol: Math.min(6, 6 - endIdxRev),
          continuesLeft: ev.date < weekStartStr,
          continuesRight: ev.endDate > weekEndStr
        };
      })
      .sort((a, b) => {
        const lenA = a.endCol - a.startCol;
        const lenB = b.endCol - b.startCol;
        if (lenB !== lenA) return lenB - lenA;
        return a.startCol - b.startCol;
      });
    const rowEnds = [];
    return candidates.map((bar) => {
      let row = 0;
      while (rowEnds[row] !== undefined && rowEnds[row] >= bar.startCol) row++;
      rowEnds[row] = bar.endCol;
      return { ...bar, row };
    });
  }, [week, events]);

  const multiDayIds = new Set(multiDayBars.map((b) => b.event.id));
  const barRowCount = multiDayBars.reduce((m, b) => Math.max(m, b.row + 1), 0);
  const BAR_HEIGHT = 20;
  const reservedSpace = barRowCount * BAR_HEIGHT;
  const weekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  // A chip is 10px text with leading-tight and py-0.5 (16.5px) plus the
  // 2px gap of space-y-0.5. Every column has the same height, so one
  // column's chip area stands in for all seven.
  const chipAreaRef = useRef(null);
  const fitRows = useFitRows(chipAreaRef, 18.5);

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
        {weekdayLabels.map((lbl, i) => (
          <div
            key={lbl}
            className={`p-1.5 text-[10px] font-medium text-center uppercase tracking-wider ${
              i >= 5 ? 'text-red-500' : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {lbl}
          </div>
        ))}
      </div>
      {/* The day grid takes the rest of the widget, so the column rules run
          to the bottom and a taller widget shows more chips instead of
          empty space below them. */}
      <div className="grid grid-cols-7 relative flex-1 min-h-0">
        {week.map((day, dayIdx) => {
          const todayFlag = isToday(day);
          const weekend = day.getDay() === 0 || day.getDay() === 6;
          const dayItems = eventsForDay(day).filter((ev) => !multiDayIds.has(ev.id));
          const bdays = dayItems.filter((ev) => ev.isBirthday);
          const rest = dayItems.filter((ev) => !ev.isBirthday);
          const firstB = bdays[0];
          const firstBCat = firstB ? getCategory(firstB.categoryId) : null;
          // Everything fits, or the last slot says how many more there are.
          const shown = rest.length > fitRows ? Math.max(0, fitRows - 1) : rest.length;
          const more = rest.length - shown;
          return (
            <div
              key={day.toISOString()}
              className="border-l border-gray-200 dark:border-gray-800 p-1 min-w-0 overflow-hidden flex flex-col"
            >
              <div className="flex items-center gap-1 mb-1 min-w-0">
                <span
                  className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 text-[11px] font-medium rounded-full ${
                    todayFlag
                      ? 'bg-primary-600 text-white'
                      : weekend
                      ? 'text-red-500'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {firstB && firstBCat && (
                  <div
                    className="flex-1 min-w-0 text-[10px] px-1 py-0.5 rounded font-semibold flex items-center gap-0.5"
                    style={{ backgroundColor: `${firstBCat.color}22`, color: firstBCat.color }}
                    title={bdays.map((b) => b.title).join(', ')}
                  >
                    <Gift size={10} className="flex-shrink-0" />
                    <span className="truncate">{firstB.title}</span>
                  </div>
                )}
              </div>
              {reservedSpace > 0 && <div className="flex-shrink-0" style={{ height: reservedSpace }} aria-hidden="true" />}
              <div className="space-y-0.5 flex-1 min-h-0 overflow-hidden" ref={dayIdx === 0 ? chipAreaRef : undefined}>
                {rest.slice(0, shown).map((ev) => {
                  const cat = getCategory(ev.categoryId);
                  const isAllDay = ev.allDay !== false;
                  if (ev.isTodo) {
                    return (
                      <div
                        key={ev.id}
                        className="text-[10px] pl-1 pr-0.5 py-0.5 rounded font-medium leading-tight flex items-center gap-0.5 overflow-hidden"
                        style={{ backgroundColor: `${cat?.color}22`, color: cat?.color }}
                        title={ev.title}
                      >
                        <CheckSquare size={9} className="flex-shrink-0" />
                        <span
                          className={`truncate flex-1 min-w-0 ${
                            ev.done ? 'line-through opacity-60' : ''
                          }`}
                        >
                          {ev.title}
                        </span>
                        {priorityEmoji(ev.priority) && (
                          <span className="flex-shrink-0 leading-none pr-0.5">
                            {priorityEmoji(ev.priority)}
                          </span>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div
                      key={ev.id}
                      className="text-[10px] px-1 py-0.5 rounded font-medium overflow-hidden leading-tight"
                      style={
                        isAllDay
                          ? { backgroundColor: cat?.color, color: '#fff' }
                          : { backgroundColor: `${cat?.color}22`, color: cat?.color }
                      }
                      title={ev.startTime ? `${ev.startTime} ${ev.title}` : ev.title}
                    >
                      <span className="fade-clip">{ev.title}</span>
                    </div>
                  );
                })}
                {more > 0 && fitRows > 0 && (
                  <div className="text-[10px] text-gray-500 font-medium px-1 leading-tight py-0.5">
                    +{more}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {multiDayBars.length > 0 && (
          <div
            className="absolute pointer-events-none left-0 right-0"
            style={{ top: 26, height: barRowCount * BAR_HEIGHT }}
          >
            {multiDayBars.map((bar) => {
              const cat = getCategory(bar.event.categoryId);
              const widthPct = ((bar.endCol - bar.startCol + 1) / 7) * 100;
              const leftPct = (bar.startCol / 7) * 100;
              return (
                <div
                  key={bar.event.id}
                  className={`absolute text-[10px] font-medium truncate px-1.5 flex items-center ${
                    bar.continuesLeft ? '' : 'rounded-l'
                  } ${bar.continuesRight ? '' : 'rounded-r'}`}
                  style={{
                    left: `calc(${leftPct}% + 2px)`,
                    width: `calc(${widthPct}% - 4px)`,
                    top: bar.row * BAR_HEIGHT,
                    height: BAR_HEIGHT - 2,
                    backgroundColor: cat?.color,
                    color: '#fff'
                  }}
                  title={bar.event.title}
                >
                  {bar.event.title}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// calendar.month — compact month grid; chips when there's room, dots when not.
// ---------------------------------------------------------------------------
const CalendarMonth = ({ data, scope, size, today, t }) => {
  const { getCategory, eventsForDay } = useCalendarScope(data, scope, today);
  const gridStart = startOfWeek(startOfMonth(today), { weekStartsOn: 1 });
  const days = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)),
    [gridStart]
  );
  const weeks = useMemo(() => {
    const out = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [days]);
  const dense = !size || size.h < 12 || size.w < 6;
  const weekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
        {weekdayLabels.map((lbl, i) => (
          <div
            key={lbl}
            className={`py-1 text-[10px] font-medium text-center uppercase ${
              i >= 5 ? 'text-red-500' : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {lbl}
          </div>
        ))}
      </div>
      <div className="flex-1 flex flex-col">
        {weeks.map((wk, wi) => (
          <div key={wi} className="grid grid-cols-7 flex-1 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
            {wk.map((day) => {
              const inMonth = isSameMonth(day, today);
              const todayFlag = isToday(day);
              const items = eventsForDay(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`border-l border-gray-100 dark:border-gray-800 p-0.5 min-w-0 overflow-hidden ${
                    inMonth ? '' : 'opacity-40'
                  }`}
                >
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium rounded-full ${
                        todayFlag
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {format(day, 'd')}
                    </span>
                  </div>
                  {dense ? (
                    <div className="flex flex-wrap gap-0.5 justify-center mt-0.5">
                      {items.slice(0, 4).map((ev) => (
                        <span
                          key={ev.id}
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: getCategory(ev.categoryId)?.color }}
                          title={ev.title}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-0.5 mt-0.5">
                      {items.slice(0, 2).map((ev) => {
                        const cat = getCategory(ev.categoryId);
                        return (
                          <div
                            key={ev.id}
                            className="text-[9px] px-1 rounded font-medium truncate leading-tight"
                            style={{ backgroundColor: `${cat?.color}22`, color: cat?.color }}
                            title={ev.title}
                          >
                            {ev.title}
                          </div>
                        );
                      })}
                      {items.length > 2 && (
                        <div className="text-[9px] text-gray-400 px-1">+{items.length - 2}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="px-3 py-1 text-[10px] text-gray-400 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 flex justify-between">
        <span className="capitalize">{fmt(today, 'monthYear')}</span>
        <span>KW {getWeek(today, { weekStartsOn: 1, firstWeekContainsDate: 4 })}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// birthdays.upcoming
// ---------------------------------------------------------------------------
const BirthdaysUpcoming = ({ data, size, today, t }) => {
  const birthdays = data?.calendar?.birthdays;
  const upcoming = useMemo(() => {
    const people = birthdays || [];
    const out = [];
    for (const p of people) {
      let day = p.day, month = p.month, year = p.year;
      if (!day && p.birthDate) {
        const parts = String(p.birthDate).split('-').map(Number);
        year = parts[0]; month = parts[1]; day = parts[2];
      }
      if (!day || !month) continue;
      // Next occurrence: this year, or next if it already passed.
      let next = new Date(today.getFullYear(), month - 1, day);
      next.setHours(0, 0, 0, 0);
      const anchor = new Date(today);
      anchor.setHours(0, 0, 0, 0);
      if (next < anchor) next = new Date(today.getFullYear() + 1, month - 1, day);
      const turns = year ? next.getFullYear() - year : null;
      out.push({ id: p.id, name: p.name, date: next, days: differenceInCalendarDays(next, anchor), turns });
    }
    return out.sort((a, b) => a.days - b.days).slice(0, isCompact(size) ? 4 : 8);
  }, [birthdays, today, size]);

  if (upcoming.length === 0) {
    return <WidgetEmpty>{t('dashw.noBirthdays', 'Keine Geburtstage eingetragen.')}</WidgetEmpty>;
  }
  return (
    <WidgetList>
      {upcoming.map((b) => (
        <WidgetRow key={b.id}>
          <Gift size={16} className="text-pink-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {b.name}
              {b.turns ? <span className="text-gray-400 font-normal"> ({b.turns})</span> : null}
            </p>
            <p className="text-xs text-gray-500">{fmt(b.date, 'dayMonthLong')}</p>
          </div>
          <span className="text-xs font-medium text-gray-500 tabular-nums flex-shrink-0">
            {b.days === 0
              ? t('common.today', 'Heute')
              : b.days === 1
              ? t('dashw.tomorrow', 'Morgen')
              : t('dashw.inDays', 'in {n} Tagen', { n: b.days })}
          </span>
        </WidgetRow>
      ))}
    </WidgetList>
  );
};

// ---------------------------------------------------------------------------
// to-dos
// ---------------------------------------------------------------------------
const PRIO_ORDER = { high: 0, medium: 1, low: 2 };

const useScopedTasks = (data, scope) =>
  useMemo(() => {
    const tasks = data?.todo?.tasks || [];
    return !scope || scope === 'all' ? tasks : tasks.filter((tk) => tk.groupId === scope);
  }, [data?.todo?.tasks, scope]);

const useToggleTask = (data, updateData) => (id) => {
  const tasks = data?.todo?.tasks || [];
  const target = tasks.find((tk) => tk.id === id);
  if (target && !target.done) playBlub();
  haptics.tap();
  updateData({
    todo: {
      ...(data.todo || {}),
      tasks: tasks.map((tk) =>
        tk.id === id
          ? {
              ...tk,
              done: !tk.done,
              completedAt: !tk.done ? new Date().toISOString() : null
            }
          : tk
      )
    }
  });
};

const TaskRows = ({ tasks, toggle, today, t }) => (
  <WidgetList>
    {tasks.map((task) => {
      let overdue = false;
      try {
        overdue = task.date && isPast(parseISO(task.date)) && !isToday(parseISO(task.date));
      } catch {
        overdue = false;
      }
      return (
        <WidgetRow key={task.id}>
          <button
            onClick={() => toggle(task.id)}
            className="text-gray-400 hover:text-primary-600 flex-shrink-0"
            title={t('todo.markDone', 'Erledigt')}
          >
            <Square size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {task.title}
            </p>
            {task.date && (
              <p className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                {fmt(parseISO(task.date), 'short')}
                {task.time && ` · ${task.time}`}
                {overdue && ` · ${t('todo.overdue', 'überfällig')}`}
              </p>
            )}
          </div>
          {task.priority && (
            <span className="flex-shrink-0 leading-none pr-0.5 text-sm">
              {priorityEmoji(task.priority)}
            </span>
          )}
        </WidgetRow>
      );
    })}
  </WidgetList>
);

const TodoOpen = ({ data, updateData, scope, today, t }) => {
  const scoped = useScopedTasks(data, scope);
  const toggle = useToggleTask(data, updateData);
  const open = useMemo(
    () =>
      scoped
        .filter((tk) => !tk.done)
        .sort((a, b) => {
          const pa = PRIO_ORDER[a.priority] ?? 3;
          const pb = PRIO_ORDER[b.priority] ?? 3;
          if (pa !== pb) return pa - pb;
          if (a.date && b.date) {
            const byDate = a.date.localeCompare(b.date);
            if (byDate) return byDate;
            return (a.time || '').localeCompare(b.time || '');
          }
          if (a.date) return -1;
          if (b.date) return 1;
          return (b.createdAt || '').localeCompare(a.createdAt || '');
        }),
    [scoped]
  );
  if (open.length === 0) {
    return <WidgetEmpty>{t('dashboard.emptyTodos', 'Keine offenen Aufgaben.')}</WidgetEmpty>;
  }
  return <TaskRows tasks={open} toggle={toggle} today={today} t={t} />;
};

const TodoToday = ({ data, updateData, scope, today, t }) => {
  const scoped = useScopedTasks(data, scope);
  const toggle = useToggleTask(data, updateData);
  const todayStr = dateStr(today);
  const items = useMemo(
    () => scoped.filter((tk) => !tk.done && tk.date && tk.date <= todayStr),
    [scoped, todayStr]
  );
  if (items.length === 0) {
    return <WidgetEmpty>{t('todo.emptyToday', 'Keine Aufgaben für heute.')}</WidgetEmpty>;
  }
  return <TaskRows tasks={items} toggle={toggle} today={today} t={t} />;
};

const TodoCount = ({ data, scope, size, t }) => {
  const scoped = useScopedTasks(data, scope);
  const open = scoped.filter((tk) => !tk.done).length;
  return (
    <StatTile
      value={open}
      label={t('dashw.openTasks', 'offene Aufgaben')}
      tone={open === 0 ? 'positive' : 'accent'}
      icon={CheckSquare}
      compact={isCompact(size)}
    />
  );
};

// ---------------------------------------------------------------------------
// shopping
// ---------------------------------------------------------------------------
const useScopedItems = (data, scope) =>
  useMemo(() => {
    const items = data?.shopping?.items || [];
    const listId = !scope || scope === 'personal' ? null : scope;
    return items.filter((it) => (it.sharedListId || null) === listId);
  }, [data?.shopping?.items, scope]);

const ShoppingListWidget = ({ data, updateData, scope, t }) => {
  const scoped = useScopedItems(data, scope);
  const open = scoped.filter((it) => !it.checked);
  const toggle = (id) => {
    haptics.tap();
    const all = data?.shopping?.items || [];
    updateData({
      shopping: {
        ...(data.shopping || {}),
        items: all.map((it) =>
          it.id === id
            ? { ...it, checked: !it.checked, checkedAt: !it.checked ? Date.now() : null }
            : it
        )
      }
    });
  };
  if (open.length === 0) {
    return <WidgetEmpty>{t('dashboard.emptyShopping', 'Einkaufsliste ist leer.')}</WidgetEmpty>;
  }
  return (
    <WidgetList>
      {open.map((it) => (
        <WidgetRow key={it.id}>
          <button
            onClick={() => toggle(it.id)}
            className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-primary-500 flex items-center justify-center flex-shrink-0 text-transparent hover:text-primary-500"
          >
            <Check size={12} strokeWidth={3} />
          </button>
          <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {it.name}
            {(it.amount || it.unit) && (
              <span className="text-gray-400 font-normal"> {it.amount} {it.unit}</span>
            )}
          </span>
        </WidgetRow>
      ))}
    </WidgetList>
  );
};

const ShoppingCount = ({ data, scope, size, t }) => {
  const scoped = useScopedItems(data, scope);
  const open = scoped.filter((it) => !it.checked).length;
  return (
    <StatTile
      value={open}
      label={t('dashw.openItems', 'offene Artikel')}
      tone={open === 0 ? 'positive' : 'accent'}
      compact={isCompact(size)}
    />
  );
};

export const PLANNING_BODIES = {
  'calendar.day': CalendarDay,
  'calendar.week': CalendarWeek,
  'calendar.month': CalendarMonth,
  'calendar.agenda': CalendarAgenda,
  'birthdays.upcoming': BirthdaysUpcoming,
  'todo.open': TodoOpen,
  'todo.today': TodoToday,
  'todo.count': TodoCount,
  'shopping.list': ShoppingListWidget,
  'shopping.count': ShoppingCount
};
